import styled from "styled-components";
import { theme } from "../../theme/semanticTheme";

const bp = theme.breakpoints || { sm: "640px" };

export const TableWrapper = styled.div`
  overflow-x: ${(p) => (p.$noHorizontalScroll ? "visible" : "auto")};
  -webkit-overflow-scrolling: touch;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.background};
  min-width: 0;
`;

export const TableContent = styled.div`
  width: ${(p) => (p.$noHorizontalScroll ? "100%" : "max-content")};
  min-width: ${(p) => (p.$noHorizontalScroll ? "auto" : "100%")};
`;

export const StyledTable = styled.table`
  width: ${(p) => (p.$noHorizontalScroll ? "100%" : "max-content")};
  min-width: ${(p) => (p.$noHorizontalScroll ? "auto" : "max(100%, 600px)")};
  border-collapse: collapse;
  font-family: ${theme.typography.fontFamily};
`;

export const Thead = styled.thead`
  background: ${theme.colors.accentLight};
  position: ${(p) => (p.$sticky ? "sticky" : "relative")};
  top: 0;
  z-index: 1;
  box-shadow: ${(p) => (p.$sticky ? `0 1px 0 0 ${theme.colors.border}` : "none")};
`;

export const Tbody = styled.tbody``;

function cellWidthExpr(w) {
  if (w == null) return "auto";
  return typeof w === "number" ? `${w}px` : w;
}

export const Th = styled.th`
  text-align: ${(p) => p.$align || "center"};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.typography.tableHeader.fontSize};
  font-weight: ${theme.typography.tableHeader.fontWeight};
  line-height: ${theme.typography.tableHeader.lineHeight};
  color: ${theme.colors.primary};
  border-bottom: 2px solid rgba(57, 0, 149, 0.25);
  white-space: nowrap;
  width: ${(p) => cellWidthExpr(p.$width)};
  cursor: ${(p) => (p.$sortable ? "pointer" : "default")};
  user-select: none;

  &:hover {
    color: ${(p) => (p.$sortable ? theme.colors.text : "inherit")};
  }
  @media (max-width: ${bp.sm}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`;

export const Td = styled.td`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.tableCell.fontSize};
  font-weight: ${theme.typography.tableCell.fontWeight};
  line-height: ${theme.typography.tableCell.lineHeight};
  color: ${theme.colors.text};
  text-align: ${(p) => p.$align || "center"};
  width: ${(p) => cellWidthExpr(p.$width)};
  vertical-align: ${(p) => (p.$wrap ? "top" : "middle")};
  white-space: ${(p) => (p.$wrap ? "normal" : "inherit")};
  word-break: ${(p) => (p.$wrap ? "break-word" : "normal")};
  overflow-wrap: ${(p) => (p.$wrap ? "break-word" : "normal")};
  max-width: ${(p) => {
    if (!p.$wrap) return p.$maxWidth != null ? cellWidthExpr(p.$maxWidth) : "none";
    if (p.$maxWidth != null) return cellWidthExpr(p.$maxWidth);
    if (p.$width != null) return cellWidthExpr(p.$width);
    return "min(14rem, 21vw)";
  }};
  @media (max-width: ${bp.sm}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`;

export const Tr = styled.tr`
  background: ${(p) => (p.$striped && p.$index % 2 === 1 ? theme.colors.backgroundAlt : theme.colors.background)};
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  &:hover {
    background: ${(p) => (p.$clickable ? theme.colors.backgroundAlt : "inherit")};
  }
`;

export const ExpandableTr = styled.tr`
  background: ${theme.colors.backgroundAlt};
  & > td {
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    border-bottom: 1px solid ${theme.colors.border};
    vertical-align: top;
  }
`;

export const PaginationWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundAlt};
  width: ${(p) => (p.$noHorizontalScroll ? "100%" : "max-content")};
  min-width: ${(p) => (p.$noHorizontalScroll ? "auto" : "max(100%, 600px)")};
  @media (max-width: ${bp.sm}) {
    padding: ${theme.spacing.md};
    gap: ${theme.spacing.md};
  }
`;

export const PaginationLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: wrap;
`;

export const PageSizeBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding-right: ${theme.spacing.xl};
  margin-right: ${theme.spacing.xl};
  border-right: 1px solid ${theme.colors.border};
  white-space: nowrap;
`;

export const PaginationInfo = styled.span`
  color: ${theme.colors.text};
  font-weight: ${theme.typography.fontWeight.medium};
`;

export const PageSizeSelect = styled.select`
  font-size: ${theme.typography.fontSize.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.background};
  color: ${theme.colors.text};
  cursor: pointer;
  min-height: 32px;
  min-width: 56px;

  &:hover {
    border-color: ${theme.colors.purple};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.purple};
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

export const Ellipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 0 ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  user-select: none;
`;

export const PageButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.background};
  color: ${theme.colors.text};
  cursor: pointer;
  min-width: 32px;

  &:hover:not(:disabled) {
    border-color: ${theme.colors.purple};
    color: ${theme.colors.purple};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[data-active="true"] {
    background: ${theme.colors.purple};
    border-color: ${theme.colors.purple};
    color: #fff;
  }
`;

export const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
`;

export const EmptyWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
`;

export const EmptyCellContent = styled.div`
  padding: ${theme.spacing.xxl};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
`;

export const ActionBtn = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.xs};
  border-radius: ${theme.borderRadius.sm};
  border: none;
  cursor: pointer;
  margin-right: ${theme.spacing.xs};
  background: ${(p) => (p.$variant === "danger" ? theme.colors.error : theme.colors.purple)};
  color: #fff;

  &:hover {
    opacity: 0.9;
  }
`;

export const CheckboxCell = styled(Td)`
  width: 40px;
  text-align: center;
  vertical-align: middle;
`;
